import { useIntl, useSelectedLocaleIntl } from '@doxyme/locale';
import { useInviteViaTextMessageModalContentStyles } from './inviteViaTextMessageModalContent.styles';
import {
  Checkbox,
  DateInput,
  FormElement,
  FormStack,
  TimeInput,
  TooltipUpgrade,
  ModalContent,
  ModalFooter,
  ModalBody,
  ModalHeader,
  ModalForm,
  PhoneInput,
} from '@doxyme/ui/components/foundations';
import { Spacer } from '@doxyme/ui/components/misc';
import { LinkS, BodyS } from '@doxyme/ui/typography';
import { useTheme } from '@doxyme/ui/theme';
import { useViewport } from '@doxyme/ui/hooks';
import { isValidDate } from '@doxyme/ui/utils';
import { Icon } from '@doxyme/icons';
import {
  useInviteViaTextMessageForm,
  INVITATION_FIELD_MAX_LENGTH,
} from './useInviteViaTextMessageForm';
import Box from '@mui/material/Box';
import {
  type ChangeEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
  useRef,
  type JSX,
  useMemo,
} from 'react';
import { useInvitationTextBox } from '../useInvitationTextBox';
import { InviteViaRateLimitMessage } from '../inviteViaRateLimitMessage/inviteViaRateLimitMessage';
import { InviteViaRateLimitTrialMessage } from '../inviteViaRateLimitTrialMessage';
import { type InviteViaTextMessageModalContentProps } from './types';
import { parsePhoneNumber } from 'libphonenumber-js/max';
import { type MessageDescriptor } from 'react-intl';
import { languagePreviouslySelectedLocalStorageKey } from '../types';
import NextLink from 'next/link';
import { LanguageDropdown } from '../languageDropdown';
import { InviteViaTextbox } from '../inviteViaTextbox';
import { usePreviouslySelected } from '../languageDropdown/usePreviouslySelected';

const NON_CUSTOMIZABLE_LANGUAGE_CODES = ['en', 'es', 'fr'];

const twilioErrors: Record<string, MessageDescriptor['id']> = {
  '30003': 'inviteViaTextMessageForm.twillio.error.30003_&_30005',
  '30005': 'inviteViaTextMessageForm.twillio.error.30003_&_30005',
  '30006': 'inviteViaTextMessageForm.twillio.error.30006',
  '21610': 'inviteViaTextMessageForm.twillio.error.21610',
  '21211': 'inviteViaTextMessageForm.twillio.error.21211',
};

const phoneCountryPreviouslySelectedStorageKey =
  '[dashboard]inviteViaTextMessagePhoneInput';

export const InviteViaTextMessageModalContent = ({
  onClose,
  onSubmit,
  phoneInputDefaultCountryIso,
  onCountryIsoChanged,
  passcode,
  roomUrl,
  providerName,
  patientConsentedToSmsDefault,
  onPatientConsentedToSms,
  rateLimit,
  formSubmissionState,
  twilioErrorCode,
  clearFormSubmissionResponseMessage,
  titleId,
  canSendCustomInvite,
  isTrustedCustomer,
  onUpgradeSubscription,
}: InviteViaTextMessageModalContentProps): JSX.Element => {
  const { formatMessage } = useIntl();

  const theme = useTheme();
  const styles = useInviteViaTextMessageModalContentStyles();

  const { isSmallViewport } = useViewport();

  // We need to pass this through to the form setup as the max length takes into account the footer text (i.e - the room url / date + time / passcode text)
  const [invitationFooterTextLength, setInvitationFooterTextLength] =
    useState(0);

  const {
    form: {
      handleSubmit,
      formState: { errors, isSubmitted, isValid },
      setValue,
      resetField,
      trigger,
      setError,
      reset,
      getValues,
    },
    fields: {
      phoneField,
      dateField,
      timeField,
      invitationField,
      consentField,
      languageField,
    },
  } = useInviteViaTextMessageForm({ invitationFooterTextLength });

  const { formatMessage: selectedLocaleFormatMessage } = useSelectedLocaleIntl(
    languageField.value,
  );

  const onLanguageChange = useCallback((value: string) => {
    languageField.onChange(value);
  }, []);

  useEffect(() => {
    setValue('consentField', patientConsentedToSmsDefault);
  }, [patientConsentedToSmsDefault]);

  const {
    invitationFooterText,
    isUsingDefaultTextContent,
    onRestoreDefaultMessage,
    invitationFormattedText,
  } = useInvitationTextBox({
    dateFieldValue:
      Boolean(errors.dateField) ||
      dateField.value === null ||
      !isValidDate(dateField.value)
        ? null
        : dateField.value,
    timeFieldValue:
      Boolean(errors.timeField) ||
      timeField.value === null ||
      !isValidDate(timeField.value)
        ? null
        : timeField.value,
    invitationFieldValue: invitationField.value,
    passcode,
    roomUrl,
    providerName,
    locale: languageField.value,
    resetField,
    formatMessage: selectedLocaleFormatMessage,
    trigger,
  });

  useEffect(() => {
    setInvitationFooterTextLength(invitationFooterText.length);
  }, [invitationFooterText]);

  /* When we submit a form successfully we revert the phone input back to the default.
     However, we still need to retain the number we sent the message too so we can use it to show the `Last sent to xxx` message.
  */
  const lastSentPhoneNumber = useRef<string | undefined>(undefined);

  const phoneFieldElement = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (formSubmissionState === 'success' || formSubmissionState === 'error') {
      // Refocus the phone input field after submitting for added convenience
      phoneFieldElement.current?.focus();
    }

    /* If there's a server side error message, we always show it against the phone field (as that's what it relates too)
       However, we can't set the message content using react hook form as it wants a raw string and some of our error messages have links.
       We instead we set the message content inline on the FormElement for Phone Input
    */
    if (formSubmissionState === 'error') {
      setError('phoneField', {
        type: 'server',
      });
    } else if (formSubmissionState === 'success') {
      // Reset the date / time / phone fields but preserve everything else such as the invitation text and consent state
      reset({
        ...getValues(),
        dateField: null,
        timeField: null,
        phoneField: '',
      });
    }
  }, [formSubmissionState]);

  const onSubmitForm = handleSubmit(async (data) => {
    lastSentPhoneNumber.current = parsePhoneNumber(
      data.phoneField,
    ).formatInternational();

    await onSubmit({
      ...data,
      isUsingDefaultInvitationTextContent: isUsingDefaultTextContent,
      invitationFormattedText,
    });
  });

  const getServerSideErrorMessage = (): ReactNode | undefined => {
    const message = formatMessage(
      {
        id:
          twilioErrorCode !== undefined &&
          twilioErrors[twilioErrorCode] !== undefined
            ? twilioErrors[twilioErrorCode]
            : 'inviteViaTextMessageForm.twillio.error.default',
      },
      {
        // If the form somehow starts in the error state, we need to ensure the comp doesn't crash
        // when parsing the phone number
        phone:
          phoneField.value !== ''
            ? parsePhoneNumber(phoneField.value).formatInternational()
            : '',
        link: (children) => (
          <NextLink
            href='https://helpcenter.doxy.me/en/articles/8272770-text-message-invites-subscribe-to-doxy-me-messaging'
            target='_blank'
            rel='noreferrer'
          >
            <LinkS>{children}</LinkS>
          </NextLink>
        ),
      },
    );

    return message;
  };

  const invitationTextDefaultValue = useMemo(() => {
    const defaultValue = selectedLocaleFormatMessage(
      {
        id: 'inviteViaTextMessageForm.invitation.invitationTextBoxDefaultValue',
      },
      {
        providerName,
      },
    );
    return `${defaultValue} `;
  }, [providerName, selectedLocaleFormatMessage]);

  const hasRateLimit = Boolean(rateLimit !== undefined);

  const [previouslySelected, updatePreviouslySelected] = usePreviouslySelected(
    phoneCountryPreviouslySelectedStorageKey,
  );

  return (
    <ModalContent>
      <ModalHeader
        onClose={onClose}
        title={formatMessage({ id: 'inviteViaTextMessage' })}
        titleId={titleId}
      />
      <ModalForm onSubmit={(e) => void onSubmitForm(e)}>
        <ModalBody>
          {rateLimit !== undefined && (
            <>
              {isTrustedCustomer ? (
                <InviteViaRateLimitMessage {...rateLimit} />
              ) : (
                <InviteViaRateLimitTrialMessage
                  {...rateLimit}
                  onUpgrade={onUpgradeSubscription}
                />
              )}
              <Spacer size={theme.spacing(8)} vertical={true} />
            </>
          )}
          <FormStack>
            <FormElement
              htmlFor={phoneField.name}
              label={formatMessage({
                id: 'inviteViaTextMessageForm.phone.label',
              })}
              errorText={
                formSubmissionState === 'error'
                  ? getServerSideErrorMessage()
                  : errors.phoneField?.message
              }
              helperText={
                formSubmissionState === 'success'
                  ? formatMessage(
                      {
                        id: 'inviteViaTextMessageForm.phone.lastSent',
                      },
                      {
                        phone: lastSentPhoneNumber.current,
                      },
                    )
                  : undefined
              }
              required={true}
            >
              <PhoneInput
                {...phoneField}
                id={phoneField.name}
                name={phoneField.name}
                autoFocus
                defaultCountry={phoneInputDefaultCountryIso}
                hasError={
                  Boolean(errors.phoneField?.message) ||
                  Boolean(
                    formSubmissionState === 'error' &&
                      getServerSideErrorMessage(),
                  )
                }
                isDisabled={hasRateLimit}
                previouslySelected={previouslySelected}
                onUpdatePreviouslySelected={updatePreviouslySelected}
                onChange={(value) => {
                  // If we've submitted the form to the server and the user has since amended the input then reset the form back to an initialised state.
                  if (
                    formSubmissionState === 'success' ||
                    formSubmissionState === 'error'
                  ) {
                    clearFormSubmissionResponseMessage();
                    lastSentPhoneNumber.current = '';
                  }

                  phoneField.onChange(value);
                }}
                onCountryIsoChanged={onCountryIsoChanged}
                onInputElement={(el) => (phoneFieldElement.current = el)}
                placeholder={formatMessage({
                  id: 'form.phoneInput.placeholder',
                })}
              />
            </FormElement>
            <FormElement
              htmlFor={dateField.name}
              label={formatMessage({
                id: 'inviteViaTextMessageForm.dateTime.label',
              })}
              errorText={errors.dateField?.message ?? errors.timeField?.message}
            >
              <Box
                sx={{
                  display: 'flex',
                  height: '100%',
                  flexDirection: isSmallViewport ? 'column' : 'row',
                  columnGap: isSmallViewport ? 0 : 4,
                  rowGap: isSmallViewport ? 4 : 0,
                }}
              >
                <DateInput
                  {...dateField}
                  id={dateField.name}
                  disabled={hasRateLimit}
                  hasError={Boolean(errors.dateField?.message)}
                  css={{ flex: '1 1 0' }}
                />
                <TimeInput
                  {...timeField}
                  disabled={hasRateLimit}
                  hasError={Boolean(errors.timeField?.message)}
                  clockPopoverPlacement={
                    isSmallViewport ? 'bottom-start' : 'bottom-end'
                  }
                  css={{ flex: '1 1 0' }}
                />
              </Box>
            </FormElement>
            <FormElement
              htmlFor={invitationField.name}
              label={formatMessage({
                id: 'inviteViaTextMessageForm.invitation.label',
              })}
              labelTooltip={
                canSendCustomInvite ? undefined : (
                  <TooltipUpgrade
                    text={formatMessage({
                      id: 'inviteViaTextMessageForm.invitation.upgradeTooltip',
                    })}
                    onClick={onUpgradeSubscription}
                  />
                )
              }
              subText={
                canSendCustomInvite
                  ? {
                      text: formatMessage({
                        id: 'inviteViaTextMessageForm.invitation.subText',
                      }),
                      tooltip: formatMessage({
                        id: 'inviteViaTextMessageForm.invitation.subTextToolTip',
                      }),
                    }
                  : undefined
              }
              rightComponent={
                <LanguageDropdown
                  previouslySelectedLocalStorageKey={
                    languagePreviouslySelectedLocalStorageKey
                  }
                  onChange={onLanguageChange}
                  disabled={hasRateLimit}
                  allowedLanguageCodes={
                    canSendCustomInvite
                      ? undefined
                      : NON_CUSTOMIZABLE_LANGUAGE_CODES
                  }
                />
              }
              actionLink={
                isUsingDefaultTextContent || !canSendCustomInvite
                  ? undefined
                  : {
                      iconComponent: (
                        <>
                          <Icon.Undo size='xsmall' />
                          <Spacer size={4} />
                        </>
                      ),
                      text: formatMessage({
                        id: 'inviteViaTextMessageForm.invitation.restoreDefaultMessage',
                      }),
                      onClick: onRestoreDefaultMessage,
                    }
              }
              /* The input char count needs to include the entire invitation text i.e. value + footer text */
              inputLength={invitationFormattedText.length}
              inputMaxLength={INVITATION_FIELD_MAX_LENGTH}
              alwaysShowCharacterCount={canSendCustomInvite}
            >
              {canSendCustomInvite ? (
                <InviteViaTextbox
                  {...invitationField}
                  id={invitationField.name}
                  staticContent={<>{invitationFooterText}</>}
                  hasError={Boolean(errors.invitationField)}
                  disabled={hasRateLimit}
                />
              ) : (
                <Box css={styles.readOnlyTextbox.wrapper}>
                  <BodyS css={styles.readOnlyTextbox.defaultMessage}>
                    {invitationTextDefaultValue}
                  </BodyS>
                  <BodyS css={styles.readOnlyTextbox.staticContent}>
                    {invitationFooterText}
                  </BodyS>
                </Box>
              )}
            </FormElement>
            <FormElement errorText={errors.consentField?.message}>
              <Checkbox
                {...consentField}
                checked={consentField.value}
                labelSx={styles.consentCheckbox}
                label={
                  <BodyS tag='span'>
                    {formatMessage(
                      {
                        id: 'inviteViaTextMessageForm.invitation.consentText',
                      },
                      {
                        link: (children) => (
                          <NextLink
                            href='https://help.doxy.me/en/articles/4217988-consent-to-send-sms-invitations'
                            target='_blank'
                            rel='noreferrer'
                          >
                            <LinkS>{children}</LinkS>
                          </NextLink>
                        ),
                      },
                    )}
                  </BodyS>
                }
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onPatientConsentedToSms(e.target.checked);
                  consentField.onChange(e);
                }}
                disabled={hasRateLimit}
                hasError={Boolean(errors.consentField)}
              />
            </FormElement>
          </FormStack>
        </ModalBody>

        <ModalFooter
          submitButton={{
            children: formatMessage({
              id:
                formSubmissionState === 'success'
                  ? 'messageSent'
                  : 'sendMessage',
            }),
            isLoading: formSubmissionState === 'submitting',
            isDisabled:
              !(!isSubmitted || isValid || formSubmissionState === 'success') ||
              Boolean(rateLimit),
            htmlType: formSubmissionState === 'success' ? 'button' : 'submit',
            status: formSubmissionState === 'success' ? 'success' : undefined,
          }}
        />
      </ModalForm>
    </ModalContent>
  );
};
